import { useState, useEffect } from "react";
import { useRouter } from "next/router";
import { useUserContext } from "context/UserContext";
import { validation } from "utils/helpers/validation";

// Map backend error codes to translation keys
const errorCopy: Record<string, string> = {
  accountDoesntExist: "login_error.account_not_found",
  invalidLoginCredential: "login_error.incorrect_password",
  bannedAccount: "login_error.account_suspended",
  deletedAccount: "login_error.account_deleted",
  invalidOtp: "login_error.invalid_code",
  otpExpired: "login_error.code_expired",
  phoneNotExisted: "login_error.account_not_found",
  emailNotExisted: "login_error.account_not_found",
  default: "login_error.generic",
};

export interface ILoginForm {
  email: string;
  password: string;
  phone: string;
  callingCode: string;
}

interface IProps {
  onLoginSuccess?: (token: string) => void;
  isRedirect?: boolean;
}

enum InputType {
  EMAIL = "email",
  PHONE = "phone",
  UNKNOWN = "unknown",
}

const useLogin = ({ isRedirect, onLoginSuccess }: IProps) => {
  const { push, query } = useRouter();
  const { login, checkExist, resetCheckExist } = useUserContext();

  const [inputValue, setInputValue] = useState("");
  const [inputType, setInputType] = useState<InputType>(InputType.UNKNOWN);

  const [errorForm, setErrorForm] = useState<ILoginForm>({
    email: "",
    phone: "",
    callingCode: "",
    password: "",
  });

  const [form, setForm] = useState<ILoginForm>({
    email: "",
    phone: "",
    callingCode: "966", // Default country code
    password: "",
  });

  const {
    mutate: loginMutate,
    isPending: isLoginPending,
    data: loginData,
  } = login;

  const {
    mutate: checkExistMutate,
    isPending: isCheckPending,
    data: checkExistData,
  } = checkExist;

  const isLoading = isLoginPending || isCheckPending;

  const updateInputType = () => {
    if (validation.isInputPhone(inputValue)) {
      setInputType(InputType.PHONE);
    } else if (validation.isInputEmail(inputValue)) {
      setInputType(InputType.EMAIL);
    } else {
      setInputType(InputType.UNKNOWN);
    }
  };

  useEffect(() => {
    updateInputType();
  }, [inputValue]);

  const processErrorMessages = (data: { message: string; payload: any }) => {
    if (!data?.message) return;

    // Handle success early
    if (data.message === "signinSuccessful" && data.payload?.token) {
      if (onLoginSuccess) {
        onLoginSuccess(data.payload.token);
      } else if (isRedirect) {
        const redirectPath = query.redirect ? query.redirect.toString() : "/";
        push(redirectPath);
      }
      return;
    }

    const msg = data.message;
    const tKey = errorCopy[msg] || errorCopy.default;

    // Decide which field to attach the error to
    let field: keyof ILoginForm | "" = "";
    if (msg === "phoneNotExisted") field = "phone";
    else if (msg === "emailNotExisted") field = "email";
    else if (msg === "invalidLoginCredential") field = "password";
    else if (msg === "accountDoesntExist") field = inputType === InputType.PHONE ? "phone" : "email";
    else if (msg === "bannedAccount" || msg === "deletedAccount") field = inputType === InputType.PHONE ? "phone" : "email";
    else if (msg === "invalidOtp" || msg === "otpExpired") field = "password"; // show near password/OTP input

    if (field) {
      setErrorForm((prev) => ({ ...prev, [field]: tKey }));
    }
  };

  useEffect(() => {
    if (loginData) {
      processErrorMessages(loginData);
    }
  }, [loginData]);

  useEffect(() => {
    if (checkExistData) {
      processErrorMessages(checkExistData);
    }
  }, [checkExistData]);

  const disableButton = () => {
    if (inputType === InputType.EMAIL) {
      return !validation.isEmailValid(form.email);
    } else if (inputType === InputType.PHONE) {
      return !validation.isPhoneValid(form.phone);
    } else {
      return true;
    }
  };

  const handleInput = (key: string, value: string) => {
    const trimmedValue = value.trim();
    if (validation.areAllowedCharsInEmailPhoneInput(trimmedValue)) {
      setForm((prev) => ({ ...prev, [key]: trimmedValue }));
      // Real-time validation for email/phone
      if (key === "email") {
        const err = trimmedValue && !validation.isEmailValid(trimmedValue) ? "email_valid_err" : "";
        setErrorForm((prev) => ({ ...prev, email: err }));
      } else if (key === "phone") {
        const err = trimmedValue && !validation.isPhoneValid(trimmedValue) ? "phone_valid_err" : "";
        setErrorForm((prev) => ({ ...prev, phone: err }));
      } else {
        setErrorForm((prev) => ({ ...prev, [key]: "" }));
      }
      if (key === "phone" || key === "email" || key === "phoneOrEmail") {
        setInputValue(trimmedValue);
      }
    }
  };

  // Real-time password validation
  const handlePasswordChange = (value: string) => {
    const trimmed = value.trim();
    setForm((prev) => ({ ...prev, password: trimmed }));
    const err = trimmed && !validation.isPasswordValid(trimmed) ? "password_valid_err" : "";
    setErrorForm((prev) => ({ ...prev, password: err }));
  };

  const onChange = (e: { target: { value: string } }) => {
    const value = e.target.value;

    // Apply validation to ensure only allowed characters are set
    if (validation.areAllowedCharsInEmailPhoneInput(value)) {
      setInputValue(value);
    } else {
      // Optionally trim the input value to only allowed characters
      const filteredValue = value
        .split("")
        .filter((char) => validation.areAllowedCharsInEmailPhoneInput(char))
        .join("");
      setInputValue(filteredValue);
    }
  };

  const updateFormError = (field: string, errorMessage: string) => {
    setErrorForm((prev) => ({ ...prev, [field]: errorMessage }));
  };

  const isValidForm = () => {
    if (inputType === InputType.PHONE) {
      return validation.isPhoneValid(form.phone);
    }
    if (inputType === InputType.EMAIL) {
      return validation.isEmailValid(form.email);
    }
    return false;
  };

  const submit = async (e: { preventDefault: () => void }) => {
    e.preventDefault();

    if (inputType === InputType.EMAIL && !validation.isEmailValid(form.email)) {
      updateFormError("email", "email_valid_err");
    }
    if (inputType === InputType.PHONE && !validation.isPhoneValid(form.phone)) {
      updateFormError("phone", "phone_valid_err");
    }

    // Only call checkExist if form is valid
    if (!checkExistData?.payload && isValidForm()) {
      if (inputType === InputType.PHONE) {
        await checkExistMutate({
          callingCode: form.callingCode,
          phone: form.phone,
        });
      }
      if (inputType === InputType.EMAIL) {
        await checkExistMutate({
          email: form.email,
        });
      }
    }

    // Proceed if user exists and the form is valid
    if (checkExistData?.payload) {
      if (!validation.isPasswordValid(form.password)) {
        updateFormError("password", "password_valid_err");
      }
    }

    if (
      isValidForm() &&
      checkExistData?.payload &&
      validation.isPasswordValid(form.password)
    ) {
      if (inputType === InputType.EMAIL) {
        loginMutate({
          email: form.email,
          password: form.password,
        });
      }
      if (inputType === InputType.PHONE) {
        loginMutate({
          phone: form.phone,
          callingCode: form.callingCode,
          password: form.password,
        });
      }
    }
  };

  const resetUserExist = () => {
    resetCheckExist();
  };

  return {
    submit,
    onChange,
    handleInput,
    handlePasswordChange,
    disableButton,
    errorForm,
    form,
    inputType,
    inputValue,
    isLoading,
    resetUserExist,
    check: {
      isLoading: isCheckPending,
      isUserExist: checkExistData?.payload || undefined,
    },
  };
};

export default useLogin;
